/**
 * Utility functions for making HTTP requests
 * @module ajaxUtils
 */

/**
 * Default request options
 * @constant {Object}
 */
const DEFAULT_OPTIONS = {
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    }
};

/**
 * Makes an HTTP request and returns the response
 * @param {string} url - The endpoint URL
 * @param {Object} options - Request configuration
 * @param {string} [options.method='GET'] - HTTP method
 * @param {Object} [options.headers] - Request headers
 * @param {Object|string} [options.data] - Request payload
 * @param {number} [options.timeout] - Request timeout in milliseconds
 * @returns {Promise<Object>} Promise that resolves with the response
 * @throws {Error} If the request fails or response isn't OK
 * 
 * @example
 * // GET request
 * const data = await ajax('/api/users');
 * 
 * // POST request with data
 * const response = await ajax('/api/tracking', {
 *   method: 'POST',
 *   data: { event: 'pageview' }
 * });
 * 
 * // Custom headers
 * const result = await ajax('/api/data', {
 *   headers: { 'Authorization': 'Bearer token' }
 * });
 */
const ajax = async (url, options = {}) => {
    try {
        const {
            method = 'GET',
            headers = {},
            data = null,
            timeout = 0
        } = options;

        const config = {
            method,
            headers: { ...DEFAULT_OPTIONS.headers, ...headers }
        };

        if (data) {
            config.body = typeof data === 'string' ? data : JSON.stringify(data);
        }

        const controller = new AbortController();
        let timeoutId;

        if (timeout > 0) {
            timeoutId = setTimeout(() => controller.abort(), timeout);
        }

        const response = await fetch(url, {
            ...config,
            signal: timeout > 0 ? controller.signal : undefined
        });

        if (timeoutId) {
            clearTimeout(timeoutId);
        }

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Handle no-content responses
        if (response.status === 204) {
            return null;
        }

        // Check if response has content before parsing JSON
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
            return await response.json();
        }

        return await response.text();

    } catch (error) {
        if (error.name === 'AbortError') {
            throw new Error(`Request timeout after ${options.timeout}ms`);
        }
        throw error;
    }
};

export { ajax };